import React, { useState } from "react";
import {
  BellIcon,
  BuildingIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  DatabaseIcon,
  EyeIcon,
  EyeOffIcon,
  InfoIcon,
  KeyIcon,
  PlugIcon,
  SaveIcon,
  SettingsIcon,
  ShieldLockIcon,
  SyncIcon,
} from "../../Components/Icons";
import "./Settings.css";

const SECTIONS = [
  { key: "general", label: "General", hint: "Language, timezone, formats", icon: SettingsIcon },
  { key: "organization", label: "Organization", hint: "Identity and contacts", icon: BuildingIcon },
  { key: "security", label: "Security", hint: "Sessions and passwords", icon: ShieldLockIcon },
  { key: "notifications", label: "Notifications", hint: "What gets sent, and how", icon: BellIcon },
  { key: "data", label: "Data & Privacy", hint: "Retention and exports", icon: DatabaseIcon },
  { key: "integrations", label: "Integrations", hint: "API keys and webhooks", icon: PlugIcon },
];

const LANGUAGES = ["Português (Angola)", "English", "Français"];
const TIMEZONES = ["(UTC+01:00) Luanda", "(UTC+00:00) Lisbon", "(UTC+02:00) Cairo"];
const DATE_FORMATS = ["DD/MM/YYYY", "YYYY-MM-DD", "MM/DD/YYYY"];
const NUMBER_FORMATS = ["1.234.567,89", "1,234,567.89"];
const CURRENCIES = ["AOA — Angolan Kwanza", "USD — US Dollar", "EUR — Euro"];
const LANDING_PAGES = ["Dashboard", "Taxpayers", "Data Quality", "Reports"];

const SESSION_TIMEOUTS = ["15 minutes", "30 minutes", "1 hour", "4 hours"];
const PASSWORD_LENGTHS = ["8 characters", "10 characters", "12 characters"];
const PASSWORD_EXPIRY = ["Never", "60 days", "90 days", "180 days"];
const LOCKOUT_ATTEMPTS = ["3 attempts", "5 attempts", "10 attempts"];

const RETENTION = ["12 months", "24 months", "5 years", "10 years"];
const EXPORT_FORMATS = ["XLSX", "CSV", "JSON"];

const NOTIFICATION_ROWS = [
  { key: "highRisk", label: "High-risk taxpayer detected" },
  { key: "dataQuality", label: "Data quality threshold breached" },
  { key: "importDone", label: "Import or sync completed" },
  { key: "importFailed", label: "Import or sync failed" },
  { key: "nifApproval", label: "NIF application awaiting approval" },
  { key: "security", label: "Security alert raised" },
];

const INITIAL = {
  language: "Português (Angola)",
  timezone: "(UTC+01:00) Luanda",
  dateFormat: "DD/MM/YYYY",
  numberFormat: "1.234.567,89",
  currency: "AOA — Angolan Kwanza",
  landingPage: "Dashboard",

  orgName: "Direção Geral dos Impostos",
  orgShort: "DGI Angola",
  orgNif: "500 000 001",
  orgEmail: "suporte@dgi.gov.ao",
  orgPhone: "+244 222 70 60 00",
  orgAddress: "Rua do Governo, Nº 1\nLuanda, Angola",

  sessionTimeout: "30 minutes",
  passwordLength: "10 characters",
  passwordExpiry: "90 days",
  lockoutAttempts: "5 attempts",
  requireMfa: true,
  requireSymbols: true,
  reuseBlock: true,
  ipAllowlist: "192.168.1.0/24\n10.0.0.0/16",

  retention: "5 years",
  anonymise: true,
  exportFormat: "XLSX",
  exportApproval: true,

  apiBase: "https://api.dgi.gov.ao/v1",
  apiKey: "dgi_live_5f3a9c21b7e84d06",
  webhookUrl: "https://hooks.dgi.gov.ao/registry",
  webhooksEnabled: true,
};

/* -------------------------------------------------------------- fields -- */

function Field({ label, hint, children, wide }) {
  return (
    <div className={`sfield${wide ? " sfield--wide" : ""}`}>
      <span className="sfield__label">{label}</span>
      {children}
      {hint && <span className="sfield__hint">{hint}</span>}
    </div>
  );
}

function Select({ value, onChange, options }) {
  return (
    <span className="select select--block">
      <select value={value} onChange={onChange}>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <ChevronDownIcon size={16} />
    </span>
  );
}

function Toggle({ checked, onChange, label, hint }) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="toggle__track" aria-hidden="true">
        <span className="toggle__thumb" />
      </span>
      <span className="toggle__text">
        <span className="toggle__label">{label}</span>
        {hint && <span className="toggle__hint">{hint}</span>}
      </span>
    </label>
  );
}

export default function Settings() {
  const [section, setSection] = useState("general");
  const [form, setForm] = useState(INITIAL);
  const [notifications, setNotifications] = useState(
    NOTIFICATION_ROWS.reduce(
      (acc, row) => ({ ...acc, [row.key]: { email: true, inApp: true } }),
      {}
    )
  );
  const [showKey, setShowKey] = useState(false);
  const [saved, setSaved] = useState(false);

  const set = (name) => (event) => {
    const { value, type, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    setSaved(false);
  };

  const toggleNotification = (key, channel) => () => {
    setNotifications((prev) => ({
      ...prev,
      [key]: { ...prev[key], [channel]: !prev[key][channel] },
    }));
    setSaved(false);
  };

  const reset = () => {
    setForm(INITIAL);
    setSaved(false);
  };

  const activeSection = SECTIONS.find((item) => item.key === section);

  /* ------------------------------------------------------------ panes -- */

  const PANES = {
    general: (
      <>
        <div className="sfield-grid">
          <Field label="Language">
            <Select
              value={form.language}
              onChange={set("language")}
              options={LANGUAGES}
            />
          </Field>
          <Field label="Timezone">
            <Select
              value={form.timezone}
              onChange={set("timezone")}
              options={TIMEZONES}
            />
          </Field>
          <Field label="Date format">
            <Select
              value={form.dateFormat}
              onChange={set("dateFormat")}
              options={DATE_FORMATS}
            />
          </Field>
          <Field label="Number format">
            <Select
              value={form.numberFormat}
              onChange={set("numberFormat")}
              options={NUMBER_FORMATS}
            />
          </Field>
          <Field label="Currency">
            <Select
              value={form.currency}
              onChange={set("currency")}
              options={CURRENCIES}
            />
          </Field>
          <Field label="Default landing page" hint="Where users arrive after sign-in.">
            <Select
              value={form.landingPage}
              onChange={set("landingPage")}
              options={LANDING_PAGES}
            />
          </Field>
        </div>
      </>
    ),

    organization: (
      <div className="sfield-grid">
        <Field label="Legal name">
          <input className="fld__input" value={form.orgName} onChange={set("orgName")} />
        </Field>
        <Field label="Short name">
          <input className="fld__input" value={form.orgShort} onChange={set("orgShort")} />
        </Field>
        <Field label="Institutional NIF">
          <input className="fld__input" value={form.orgNif} onChange={set("orgNif")} />
        </Field>
        <Field label="Support email">
          <input
            type="email"
            className="fld__input"
            value={form.orgEmail}
            onChange={set("orgEmail")}
          />
        </Field>
        <Field label="Support phone">
          <input className="fld__input" value={form.orgPhone} onChange={set("orgPhone")} />
        </Field>
        <Field label="Registered address" wide>
          <textarea
            className="fld__input fld__textarea"
            rows={3}
            value={form.orgAddress}
            onChange={set("orgAddress")}
          />
        </Field>
      </div>
    ),

    security: (
      <>
        <div className="sfield-grid">
          <Field label="Session timeout" hint="Idle time before users are signed out.">
            <Select
              value={form.sessionTimeout}
              onChange={set("sessionTimeout")}
              options={SESSION_TIMEOUTS}
            />
          </Field>
          <Field label="Minimum password length">
            <Select
              value={form.passwordLength}
              onChange={set("passwordLength")}
              options={PASSWORD_LENGTHS}
            />
          </Field>
          <Field label="Password expiry">
            <Select
              value={form.passwordExpiry}
              onChange={set("passwordExpiry")}
              options={PASSWORD_EXPIRY}
            />
          </Field>
          <Field label="Lock account after">
            <Select
              value={form.lockoutAttempts}
              onChange={set("lockoutAttempts")}
              options={LOCKOUT_ATTEMPTS}
            />
          </Field>
        </div>

        <div className="toggles">
          <Toggle
            checked={form.requireMfa}
            onChange={set("requireMfa")}
            label="Require two-factor authentication"
            hint="All users must confirm a one-time code at sign-in."
          />
          <Toggle
            checked={form.requireSymbols}
            onChange={set("requireSymbols")}
            label="Require symbols and numbers in passwords"
          />
          <Toggle
            checked={form.reuseBlock}
            onChange={set("reuseBlock")}
            label="Block reuse of the last 5 passwords"
          />
        </div>

        <Field
          label="IP allowlist"
          hint="One CIDR range per line. Leave empty to allow any address."
          wide
        >
          <textarea
            className="fld__input fld__textarea"
            rows={3}
            value={form.ipAllowlist}
            onChange={set("ipAllowlist")}
          />
        </Field>
      </>
    ),

    notifications: (
      <>
        <p className="settings__lede">
          Choose how each event reaches the people who need it.
        </p>
        <div className="ntable">
          <div className="ntable__head">
            <span>Event</span>
            <span>Email</span>
            <span>In-app</span>
          </div>
          {NOTIFICATION_ROWS.map((row) => (
            <div className="ntable__row" key={row.key}>
              <span className="ntable__label">{row.label}</span>
              <label className="ntable__cell">
                <input
                  type="checkbox"
                  checked={notifications[row.key].email}
                  onChange={toggleNotification(row.key, "email")}
                  aria-label={`Email for ${row.label}`}
                />
              </label>
              <label className="ntable__cell">
                <input
                  type="checkbox"
                  checked={notifications[row.key].inApp}
                  onChange={toggleNotification(row.key, "inApp")}
                  aria-label={`In-app for ${row.label}`}
                />
              </label>
            </div>
          ))}
        </div>
      </>
    ),

    data: (
      <>
        <div className="sfield-grid">
          <Field
            label="Audit log retention"
            hint="Older audit events are archived, not deleted."
          >
            <Select
              value={form.retention}
              onChange={set("retention")}
              options={RETENTION}
            />
          </Field>
          <Field label="Default export format">
            <Select
              value={form.exportFormat}
              onChange={set("exportFormat")}
              options={EXPORT_FORMATS}
            />
          </Field>
        </div>

        <div className="toggles">
          <Toggle
            checked={form.anonymise}
            onChange={set("anonymise")}
            label="Anonymise taxpayer identifiers in exports"
            hint="NIFs are masked unless the export is approved for full detail."
          />
          <Toggle
            checked={form.exportApproval}
            onChange={set("exportApproval")}
            label="Require approval for exports over 10,000 rows"
          />
        </div>

        <p className="settings__note">
          <InfoIcon size={17} />
          Retention rules are enforced by the audit service. Changes take effect
          at the next nightly run.
        </p>
      </>
    ),

    integrations: (
      <>
        <div className="sfield-grid">
          <Field label="API base URL">
            <input className="fld__input" value={form.apiBase} onChange={set("apiBase")} />
          </Field>
          <Field label="Webhook endpoint">
            <input
              className="fld__input"
              value={form.webhookUrl}
              onChange={set("webhookUrl")}
            />
          </Field>
        </div>

        <Field label="API key" hint="Rotating the key invalidates the previous one." wide>
          <span className="keyfield">
            <KeyIcon size={17} />
            <input
              className="fld__input"
              type={showKey ? "text" : "password"}
              value={form.apiKey}
              onChange={set("apiKey")}
              readOnly
            />
            <button
              type="button"
              className="keyfield__toggle"
              aria-label={showKey ? "Hide API key" : "Show API key"}
              onClick={() => setShowKey((prev) => !prev)}
            >
              {showKey ? <EyeOffIcon size={17} /> : <EyeIcon size={17} />}
            </button>
            <button type="button" className="dgi-btn dgi-btn--ghost dgi-btn--sm">
              <SyncIcon size={15} />
              Rotate
            </button>
          </span>
        </Field>

        <div className="toggles">
          <Toggle
            checked={form.webhooksEnabled}
            onChange={set("webhooksEnabled")}
            label="Send webhooks for registry events"
            hint="Delivers taxpayer, import and alert events to the endpoint above."
          />
        </div>
      </>
    ),
  };

  return (
    <div className="page">
      {/* --------------------------------------------------------- head -- */}
      <div className="page__head">
        <div>
          <h1 className="page__title">Settings</h1>
          <p className="page__subtitle">
            Configure system preferences, security policy and integrations for
            the DGI Smart Tax Registry.
          </p>
        </div>
        <div className="page__actions">
          <button type="button" className="dgi-btn dgi-btn--ghost" onClick={reset}>
            <SyncIcon size={17} />
            Reset changes
          </button>
          <button
            type="button"
            className="dgi-btn dgi-btn--primary"
            onClick={() => setSaved(true)}
          >
            <SaveIcon size={17} />
            Save Changes
          </button>
        </div>
      </div>

      {saved && (
        <p className="settings__saved">
          <CheckCircleIcon size={17} />
          Settings saved. (Not yet persisted — the settings API is pending.)
        </p>
      )}

      <div className="settings">
        {/* --------------------------------------------------- section nav -- */}
        <nav className="dgi-card settings__nav" aria-label="Settings sections">
          {SECTIONS.map((item) => {
            const Icon = item.icon;
            const active = section === item.key;
            return (
              <button
                key={item.key}
                type="button"
                className={`settings__navitem${
                  active ? " settings__navitem--active" : ""
                }`}
                aria-current={active ? "true" : undefined}
                onClick={() => setSection(item.key)}
              >
                <span className="settings__navicon">
                  <Icon size={18} />
                </span>
                <span>
                  <span className="settings__navlabel">{item.label}</span>
                  <span className="settings__navhint">{item.hint}</span>
                </span>
              </button>
            );
          })}
        </nav>

        {/* ------------------------------------------------------- panel -- */}
        <section className="dgi-card settings__panel">
          <div className="dgi-card__header">
            <h2 className="dgi-card__title">{activeSection.label}</h2>
          </div>
          {PANES[section]}
        </section>
      </div>
    </div>
  );
}
